import styled from '@emotion/styled';
import { faCaretUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Badge from '~/common/components/Badge';
import Box from '~/common/components/Box';
import HighlightedText from '~/common/components/HighlightedText';
import StatusIcon from '~/common/components/Icons/StatusIcon';
import useLeftDrawerContext from '~/common/components/Nav/LeftDrawer/context/useLeftDrawerContext';
import Text from '~/common/components/Texts/Text';
import { ALERT_TYPES, RULE_RESULT_STATUS_ID } from '~/common/constants';
import { useUser } from '~/common/hooks/useAuthentication';
import { useNavSearchStore } from '~/stores/useNavSearchStore';

import { alertStateAttributes } from './alertStateAttributes';
import { HomeActions } from './redux';

const StyledParentAlert = styled<any>('button')`
  cursor: pointer;
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 72px;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  padding-left: 0.5rem;
  padding-right: 0.25rem;
  transition: all 0.5s ease-in-out;
  border-bottom: ${(props) =>
    props.hasBadge && props.isExpanded ? 'none' : '1px solid'};
  border-bottom-color: ${(props) => props.theme.colors?.gray[200]};
  background-color: ${(props) =>
    props.selected && (!props.hasChildren || !props.isExpanded)
      ? props.theme.colors?.blue[100]
      : props.theme.colors?.white};
  overflow: hidden;
  &:hover {
    background-color: ${(props) => props.theme.colors?.blue[50]};
  }
  &:focus {
    box-shadow: none;
  }
`;

const StyledSortDownIcon = styled<any>(FontAwesomeIcon)((props) => ({
  transformOrigin: 'center',
  transition: 'transform 0.2s',
  transform: props.showParentChildren ? 'rotate(0deg)' : 'rotate(-180deg)',
  marginRight: '1rem',
}));

const isSelected = (alert, selectedAlert) => {
  if (alert?.gutterItemChildren?.length === 0) {
    return selectedAlert?.gutterItemId === alert?.gutterItemId;
  }
  return alert?.gutterItemChildren
    ?.map((child) => child?.gutterItemId)
    ?.includes(selectedAlert?.gutterItemId);
};

type ParentAlertProps = {
  alert?: any;
  badgeCount?: string | number;
  status?: (typeof RULE_RESULT_STATUS_ID)[keyof typeof RULE_RESULT_STATUS_ID];
};

const ParentAlert = ({
  alert,
  badgeCount,
  status,
}: Readonly<ParentAlertProps>) => {
  const dispatch = useDispatch();
  const searchQuery = useNavSearchStore((s) => s.searchQuery);
  const { showParentChildren, handleLeftDrawerCollapseForParentAlert } =
    useLeftDrawerContext();
  const { isUnderwriter, hasAccessToUnderwriterView } = useUser();
  const tabKey = alert?.gutterItemId;
  const { selectedAlert } = useSelector(({ home }) => home);
  const alertStatus = status ?? alert?.ruleResultStatusId;
  const hasChildren = alert?.gutterItemChildren?.length > 0;

  useEffect(() => {
    if (selectedAlert?.gutterItemId === alert?.gutterItemId) {
      dispatch(HomeActions.setSelectedAlertParent(selectedAlert));
    }
  }, [alert, dispatch, selectedAlert]);

  return (
    <StyledParentAlert
      title={alert?.displayTitle}
      data-testid={`parent-alert-${alert?.gutterItemId}`}
      role="tab"
      onClick={() => {
        handleLeftDrawerCollapseForParentAlert(
          alert,
          selectedAlert as any,
          dispatch as any,
        );
      }}
      selected={isSelected(alert, selectedAlert)}
      aria-label={alert?.displayTitle}
      aria-selected={isSelected(alert, selectedAlert)}
      {...alertStateAttributes(alert, selectedAlert, alertStatus)}
      data-tracking={`Parent-${alert?.displayTitle}`}
      aria-expanded={hasChildren ? !!showParentChildren[tabKey] : undefined}
      hasChildren={hasChildren}
      isExpanded={showParentChildren[tabKey]}
      hasBadge={!!badgeCount}
    >
      {badgeCount ? (
        <Badge
          content={badgeCount}
          color={alertStatus === RULE_RESULT_STATUS_ID.FIRED ? 'red' : 'green'}
        />
      ) : (
        <StatusIcon
          type={ALERT_TYPES.PARENT}
          status={alertStatus}
          canSeeVerification={isUnderwriter || hasAccessToUnderwriterView}
        />
      )}
      <Text
        as="h3"
        marginLeft="0.5rem"
        sx={{
          ml: 2,
          maxWidth: '131px',
          minWidth: '131px',
          textAlign: 'left',
          letterSpacing: '0px',
          mr: 2,
          overflowWrap: 'break-word',
          whiteSpace: 'pre-wrap',
          lineHeight: '1rem',
          opacity: 1,
          transform: 'translateX(0)',
          transition: 'all 0.55s ease-in-out',
          pointerEvents: 'auto',
        }}
      >
        <HighlightedText text={alert?.displayTitle ?? ''} query={searchQuery} />
      </Text>
      <Box flexGrow={1}></Box>
      {hasChildren && (
        <StyledSortDownIcon
          showParentChildren={showParentChildren[tabKey]}
          icon={faCaretUp}
        />
      )}
    </StyledParentAlert>
  );
};

export default ParentAlert;
